// Einstellungen-Ansicht (Issue #86): Benutzer-/Rollenverwaltung (nur Admin) und das eigene Profil
// (jede Rolle). Spricht ausschließlich den API-Client; das Backend erzwingt die Rechte serverseitig
// (`require("admin")`) und schützt den letzten Admin (409, code `last_admin`). Diese Ansicht spiegelt
// diese Regeln nur in der UI wider — sie erzeugt keine eigene Autorisierung.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { LlmConfigPanel } from './LlmConfigPanel';
import { useStore } from '../state/store';
import { ApiError } from '../api/client';
import {
  activateMfa,
  changeMyPassword,
  createUser,
  deleteUser,
  disableMfa,
  enrollMfa,
  getMe,
  getMyMfa,
  listProjects,
  listUsers,
  rotateUserToken,
  setProjectFourEyes,
  setUserPassword,
  updateUser,
} from '../api/client';
import {
  ROLES,
  type MfaEnrollResponse,
  type ProjectSummary,
  type UserInfo,
} from '../api/types';
import { Button } from '../components/ui';

// ── Rollen-Auswahl (Checkbox-Gruppe) ────────────────────────────────────────────────────
function RolePicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (role: string) => void;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap gap-2">
      {ROLES.map((role) => {
        const on = selected.includes(role);
        return (
          <label
            key={role}
            className={
              'flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-xs ' +
              (on ? 'border-bf-gold text-bf-white' : 'border-bf-border text-bf-muted')
            }
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => onToggle(role)}
              aria-label={t(`settings.role.${role}`)}
              className="accent-bf-gold"
            />
            {t(`settings.role.${role}`)}
          </label>
        );
      })}
    </div>
  );
}

// ── Eine Benutzerzeile mit Inline-Aktionen ──────────────────────────────────────────────
function UserRow({
  user,
  onChanged,
}: {
  user: UserInfo;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [editing, setEditing] = useState(false);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const [resetting, setResetting] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Übersetzt den serverseitigen „letzter Admin"-Schutz (409 code=last_admin) in eine klare Meldung.
  const failWith = useCallback(
    (err: unknown, fallbackKey: string) => {
      if (err instanceof ApiError && err.code === 'last_admin') {
        notify('error', t('settings.lastAdmin'));
        return;
      }
      reportError(err, t(fallbackKey));
    },
    [notify, reportError, t],
  );

  const toggleRole = (role: string) =>
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const saveRoles = async () => {
    setBusy(true);
    try {
      await updateUser(user.identity, { roles });
      notify('info', t('settings.rolesSaved', { identity: user.identity }));
      setEditing(false);
      onChanged();
    } catch (err) {
      failWith(err, 'settings.updateFailed');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if (!newPw) return;
    setBusy(true);
    try {
      await setUserPassword(user.identity, newPw);
      notify('info', t('settings.passwordSet', { identity: user.identity }));
      setNewPw('');
      setResetting(false);
      onChanged();
    } catch (err) {
      failWith(err, 'settings.setPasswordFailed');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    try {
      const res = await rotateUserToken(user.identity);
      setRotatedToken(res.token);
      onChanged();
    } catch (err) {
      failWith(err, 'settings.rotateFailed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('settings.confirmRemove', { identity: user.identity }))) return;
    setBusy(true);
    try {
      await deleteUser(user.identity);
      notify('info', t('settings.removed', { identity: user.identity }));
      onChanged();
    } catch (err) {
      failWith(err, 'settings.removeFailed');
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    if (!rotatedToken) return;
    try {
      await navigator.clipboard?.writeText(rotatedToken);
      notify('info', t('settings.tokenCopied'));
    } catch {
      /* Clipboard nicht verfügbar — Token bleibt sichtbar zum manuellen Kopieren */
    }
  };

  return (
    <tr className="border-t border-bf-border align-top" data-testid={`user-row-${user.identity}`}>
      <td className="px-3 py-2 font-mono text-sm text-bf-white">{user.identity}</td>
      <td className="px-3 py-2 text-sm text-bf-muted">
        {editing ? (
          <div className="flex flex-col gap-2">
            <RolePicker selected={roles} onToggle={toggleRole} />
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => void saveRoles()}
                disabled={busy}
                testId={`save-roles-${user.identity}`}
              >
                {t('settings.saveRoles')}
              </Button>
              <Button onClick={() => { setEditing(false); setRoles(user.roles); }} disabled={busy}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <span>{user.roles.map((r) => t(`settings.role.${r}`)).join(', ') || '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-bf-muted">
        <div>{user.disabled ? t('settings.disabled') : t('settings.active')}</div>
        {user.hasPassword ? <div>{t('settings.hasPassword')}</div> : null}
        {user.hasToken ? <div>{t('settings.hasToken')}</div> : null}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-2">
          {!editing && (
            <Button onClick={() => { setEditing(true); setRoles(user.roles); }} testId={`edit-roles-${user.identity}`}>
              {t('settings.editRoles')}
            </Button>
          )}
          <Button onClick={() => setResetting((v) => !v)} testId={`reset-pw-${user.identity}`}>
            {t('settings.resetPassword')}
          </Button>
          <Button onClick={() => void rotate()} disabled={busy} testId={`rotate-${user.identity}`}>
            {t('settings.rotateToken')}
          </Button>
          <Button
            variant="danger"
            onClick={() => void remove()}
            disabled={busy}
            testId={`delete-${user.identity}`}
          >
            {t('settings.remove')}
          </Button>
        </div>
        {resetting && (
          <div className="mt-2 flex items-end gap-2">
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">{t('settings.newPassword')}</span>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                aria-label={t('settings.newPassword')}
                className="rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
              />
            </label>
            <Button
              variant="primary"
              onClick={() => void submitPassword()}
              disabled={busy || !newPw}
              testId={`set-pw-${user.identity}`}
            >
              {t('settings.setPassword')}
            </Button>
          </div>
        )}
        {rotatedToken && (
          <div className="mt-2 rounded border border-bf-gold/50 bg-bf-navy p-2 text-xs">
            <div className="mb-1 text-bf-muted">
              {t('settings.tokenRotated', { identity: user.identity })}
            </div>
            <div className="flex items-center gap-2">
              <code
                className="break-all font-mono text-bf-white"
                data-testid={`rotated-token-${user.identity}`}
              >
                {rotatedToken}
              </code>
              <Button onClick={() => void copyToken()}>{t('settings.copyToken')}</Button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Benutzer-Verwaltung (Liste + Anlegen) ───────────────────────────────────────────────
function UserManagement(): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [newIdentity, setNewIdentity] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRoles, setNewRoles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await listUsers();
      setUsers(res.users);
    } catch (err) {
      reportError(err, t('settings.loadUsersFailed'));
    } finally {
      setLoaded(true);
    }
  }, [reportError, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggleNewRole = (role: string) =>
    setNewRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const submitCreate = async () => {
    const identity = newIdentity.trim();
    if (!identity) return;
    setBusy(true);
    try {
      await createUser({
        identity,
        password: newPassword || undefined,
        roles: newRoles,
      });
      notify('info', t('settings.created', { identity }));
      setNewIdentity('');
      setNewPassword('');
      setNewRoles([]);
      await reload();
    } catch (err) {
      reportError(err, t('settings.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-sm font-semibold text-bf-white">{t('settings.usersHeading')}</h2>
        <p className="text-xs text-bf-muted">{t('settings.usersIntro')}</p>
      </div>

      {/* Anlegen */}
      <div className="rounded border border-bf-border p-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-bf-muted">
          {t('settings.createHeading')}
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.createIdentity')}</span>
            <input
              type="text"
              value={newIdentity}
              onChange={(e) => setNewIdentity(e.target.value)}
              placeholder={t('settings.createIdentityPlaceholder')}
              aria-label={t('settings.createIdentity')}
              className="rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.initialPassword')}</span>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('settings.initialPasswordPlaceholder')}
              aria-label={t('settings.initialPassword')}
              className="rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
            />
          </label>
        </div>
        <div className="mt-3">
          <span className="mb-1 block text-xs text-bf-muted">{t('settings.roles')}</span>
          <RolePicker selected={newRoles} onToggle={toggleNewRole} />
        </div>
        <div className="mt-3">
          <Button
            variant="primary"
            onClick={() => void submitCreate()}
            disabled={busy || !newIdentity.trim()}
            testId="create-user"
          >
            {t('settings.create')}
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="overflow-x-auto rounded border border-bf-border">
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-bf-muted">
              <th className="px-3 py-2 font-semibold">{t('settings.identity')}</th>
              <th className="px-3 py-2 font-semibold">{t('settings.roles')}</th>
              <th className="px-3 py-2 font-semibold">{t('settings.statusColumn')}</th>
              <th className="px-3 py-2 font-semibold">{t('settings.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow key={u.identity} user={u} onChanged={() => void reload()} />
            ))}
            {loaded && users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-sm text-bf-muted">
                  {t('settings.noUsers')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Vier-Augen pro Projekt (nur Admin, Issue #114) ──────────────────────────────────────
// Listet die Projekte mit einem Umschalter je Projekt. Der Klick ruft setProjectFourEyes und
// spiegelt danach den vom Backend zurückgemeldeten Zustand. Die harte Durchsetzung (Rolle admin)
// liegt beim Backend (403); diese Ansicht ist nur für Admins erreichbar.
function FourEyesManagement(): JSX.Element {
  const { t } = useI18n();
  const { reportError } = useStore();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await listProjects();
      setProjects(res.projects);
    } catch (err) {
      reportError(err, t('settings.fourEyesLoadFailed'));
    } finally {
      setLoaded(true);
    }
  }, [reportError, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggle = async (project: ProjectSummary) => {
    const next = !project.fourEyes;
    setBusy(project.id);
    try {
      const res = await setProjectFourEyes(project.id, next);
      // Den vom Backend zurückgemeldeten Zustand übernehmen (maßgeblich).
      setProjects((prev) =>
        prev.map((p) => (p.id === res.id ? { ...p, fourEyes: res.fourEyes } : p)),
      );
    } catch (err) {
      reportError(err, t('settings.fourEyesSaveFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-sm font-semibold text-bf-white">{t('settings.fourEyesHeading')}</h2>
        <p className="text-xs text-bf-muted">{t('settings.fourEyesIntro')}</p>
      </div>

      <div className="overflow-x-auto rounded border border-bf-border">
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-bf-muted">
              <th className="px-3 py-2 font-semibold">{t('settings.fourEyesProject')}</th>
              <th className="px-3 py-2 font-semibold">{t('settings.fourEyesColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => {
              const on = p.fourEyes === true;
              return (
                <tr
                  key={p.id}
                  className="border-t border-bf-border align-middle"
                  data-testid={`four-eyes-row-${p.id}`}
                >
                  <td className="px-3 py-2 text-sm text-bf-white">
                    {p.name}
                    <span className="ml-2 font-mono text-xs text-bf-muted">{p.id}</span>
                  </td>
                  <td className="px-3 py-2">
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy === p.id}
                        onChange={() => void toggle(p)}
                        aria-label={t('settings.fourEyesToggle', { project: p.name })}
                        data-testid={`four-eyes-toggle-${p.id}`}
                        className="accent-bf-gold"
                      />
                      <span className="text-bf-muted">
                        {on ? t('settings.fourEyesOn') : t('settings.fourEyesOff')}
                      </span>
                    </label>
                  </td>
                </tr>
              );
            })}
            {loaded && projects.length === 0 && (
              <tr>
                <td colSpan={2} className="px-3 py-4 text-center text-sm text-bf-muted">
                  {t('settings.fourEyesNoProjects')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Eigenes Profil (jede Rolle) ─────────────────────────────────────────────────────────
// ── Zwei-Faktor (TOTP) des eigenen Kontos (Issue #192) ────────────────────────────────────
// Kopplung: enroll → Secret + otpauth-URI (QR/Manuell) → activate mit dem ersten Code → aktiv +
// Recovery-Codes (einmalig). disable trennt die Kopplung (mit Passwort). Das Backend erzwingt die
// Regeln; diese UI spiegelt sie nur. Das Secret erscheint nur während der Kopplung, die
// Recovery-Codes nur unmittelbar nach der Aktivierung.
function MfaSettings({
  enabled,
  onChanged,
}: {
  enabled: boolean;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [enroll, setEnroll] = useState<MfaEnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [disablePw, setDisablePw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyMfa()
      .then((s) => !cancelled && setRemaining(s.recoveryCodesRemaining))
      .catch(() => {
        /* Status nicht ladbar (z. B. statisches Token) — dann bleibt „—" */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const startEnroll = async () => {
    setBusy(true);
    try {
      setEnroll(await enrollMfa());
      setCode('');
    } catch (err) {
      reportError(err, t('settings.mfaEnrollFailed'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await activateMfa(code.trim());
      setRecovery(res.recoveryCodes);
      setEnroll(null);
      setCode('');
      notify('info', t('settings.mfaEnabledNotice'));
      onChanged();
    } catch (err) {
      reportError(err, t('settings.mfaActivateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    if (!disablePw) return;
    setBusy(true);
    try {
      await disableMfa(disablePw);
      setDisablePw('');
      setRecovery(null);
      notify('info', t('settings.mfaDisabled'));
      onChanged();
    } catch (err) {
      reportError(err, t('settings.mfaDisableFailed'));
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white ' +
    'outline-none focus:border-bf-gold';

  return (
    <div className="rounded border border-bf-border p-3" data-testid="mfa-settings">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-bf-muted">
        {t('settings.mfaHeading')}
      </h3>
      <p className="mb-2 text-xs text-bf-muted">{t('settings.mfaIntro')}</p>
      <p className="mb-3 text-sm text-bf-white" data-testid="mfa-status">
        {enabled ? t('settings.mfaStatusOn') : t('settings.mfaStatusOff')}
        {enabled && remaining != null
          ? ' ' + t('settings.mfaRecoveryLeft', { count: remaining })
          : ''}
      </p>

      {/* Frisch erzeugte Recovery-Codes (nur einmalig nach der Aktivierung sichtbar). */}
      {recovery && (
        <div className="mb-3 rounded border border-bf-gold/40 bg-bf-navy p-3" data-testid="mfa-recovery">
          <h4 className="mb-1 text-xs font-semibold text-bf-gold">
            {t('settings.mfaRecoveryHeading')}
          </h4>
          <p className="mb-2 text-xs text-bf-muted">{t('settings.mfaRecoveryIntro')}</p>
          <ul className="mb-2 grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-sm text-bf-white">
            {recovery.map((rc) => (
              <li key={rc}>{rc}</li>
            ))}
          </ul>
          <Button variant="secondary" onClick={() => setRecovery(null)} testId="mfa-recovery-done">
            {t('settings.mfaRecoveryDone')}
          </Button>
        </div>
      )}

      {/* Nicht aktiv: entweder Einrichtung starten oder den Enrollment-Dialog zeigen. */}
      {!enabled && !enroll && (
        <Button
          variant="primary"
          onClick={() => void startEnroll()}
          disabled={busy}
          testId="mfa-enable"
        >
          {t('settings.mfaEnable')}
        </Button>
      )}

      {!enabled && enroll && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-bf-muted">{t('settings.mfaEnrollHint')}</p>
          {/* Ohne QR-Bibliothek (Offline-Appliance): der otpauth-URI und das Secret zum Scannen
              per Kamera-App bzw. zur manuellen Eingabe. Beide sind auswählbar/kopierbar. */}
          <code
            className="block break-all rounded border border-bf-border bg-bf-navy p-2 text-xs text-bf-white"
            data-testid="mfa-otpauth"
          >
            {enroll.otpauthUri}
          </code>
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.mfaSecretLabel')}</span>
            <code className="block break-all font-mono text-sm text-bf-white" data-testid="mfa-secret">
              {enroll.secret}
            </code>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.mfaConfirmCode')}</span>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-label={t('settings.mfaConfirmCode')}
              className={inputClass}
            />
          </label>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => void confirm()}
              disabled={busy || !code.trim()}
              testId="mfa-confirm"
            >
              {t('settings.mfaConfirm')}
            </Button>
            <Button variant="secondary" onClick={() => setEnroll(null)} testId="mfa-cancel">
              {t('settings.mfaCancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Aktiv: mit Passwort wieder abschalten. */}
      {enabled && (
        <div className="flex flex-col gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">
              {t('settings.mfaDisableConfirmPassword')}
            </span>
            <input
              type="password"
              value={disablePw}
              onChange={(e) => setDisablePw(e.target.value)}
              aria-label={t('settings.mfaDisableConfirmPassword')}
              className={inputClass}
            />
          </label>
          <div>
            <Button
              variant="secondary"
              onClick={() => void turnOff()}
              disabled={busy || !disablePw}
              testId="mfa-disable"
            >
              {t('settings.mfaDisable')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MyProfile({
  me,
  onMfaChanged,
}: {
  me: UserInfo | null;
  onMfaChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current || !next) return;
    setBusy(true);
    try {
      await changeMyPassword(current, next);
      notify('info', t('settings.passwordChanged'));
      setCurrent('');
      setNext('');
    } catch (err) {
      reportError(err, t('settings.changePasswordFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex max-w-md flex-col gap-4">
      <h2 className="text-sm font-semibold text-bf-white">{t('settings.profileHeading')}</h2>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-bf-muted">{t('settings.myIdentity')}</dt>
        <dd className="font-mono text-bf-white" data-testid="profile-identity">
          {me?.identity ?? '—'}
        </dd>
        <dt className="text-bf-muted">{t('settings.myRoles')}</dt>
        <dd className="text-bf-white" data-testid="profile-roles">
          {me?.roles.map((r) => t(`settings.role.${r}`)).join(', ') || '—'}
        </dd>
      </dl>

      <div className="rounded border border-bf-border p-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-bf-muted">
          {t('settings.changePassword')}
        </h3>
        <div className="flex flex-col gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.currentPassword')}</span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              aria-label={t('settings.currentPassword')}
              className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">{t('settings.newPassword')}</span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-label={t('settings.newPassword')}
              className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
            />
          </label>
          <div>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={busy || !current || !next}
              testId="change-my-password"
            >
              {t('settings.changePasswordButton')}
            </Button>
          </div>
        </div>
      </div>

      <MfaSettings enabled={Boolean(me?.mfaEnabled)} onChanged={onMfaChanged} />
    </section>
  );
}

// ── Ansicht ─────────────────────────────────────────────────────────────────────────────
// Die Ansicht ist für Admins um die Benutzer-Verwaltung erweitert; das Profil steht jeder Rolle
// offen. Die Rolle wird aus getMe() abgeleitet (der aufrufende Tab ist nur für Admins sichtbar,
// die Ansicht selbst degradiert aber sauber, falls ein Nicht-Admin sie doch erreicht).
export function SettingsView(): JSX.Element {
  const { t } = useI18n();
  const { reportError, activeProjectId } = useStore();
  const [me, setMe] = useState<UserInfo | null>(null);
  const [tab, setTab] = useState<'users' | 'fourEyes' | 'llm' | 'profile'>('users');

  const loadMe = useCallback(() => {
    getMe()
      .then((info) => setMe(info))
      .catch((err) => reportError(err, t('settings.loadMeFailed')));
  }, [reportError, t]);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((info) => !cancelled && setMe(info))
      .catch((err) => !cancelled && reportError(err, t('settings.loadMeFailed')));
    return () => {
      cancelled = true;
    };
  }, [reportError, t]);

  const isAdmin = useMemo(() => (me?.roles ?? []).includes('admin'), [me]);

  // Nicht-Admins sehen nur ihr Profil.
  const effectiveTab = isAdmin ? tab : 'profile';

  return (
    <div className="flex h-full flex-col overflow-auto p-6">
      <h1 className="mb-4 text-lg font-bold text-bf-white">{t('settings.heading')}</h1>

      {isAdmin && (
        <div className="mb-4 flex gap-1 border-b border-bf-border" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'users'}
            data-testid="settings-tab-users"
            onClick={() => setTab('users')}
            className={
              'px-4 py-2 text-sm transition-colors ' +
              (effectiveTab === 'users'
                ? 'border-b-2 border-bf-gold text-bf-white'
                : 'border-b-2 border-transparent text-bf-muted hover:text-bf-white')
            }
          >
            {t('settings.tabUsers')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'fourEyes'}
            data-testid="settings-tab-four-eyes"
            onClick={() => setTab('fourEyes')}
            className={
              'px-4 py-2 text-sm transition-colors ' +
              (effectiveTab === 'fourEyes'
                ? 'border-b-2 border-bf-gold text-bf-white'
                : 'border-b-2 border-transparent text-bf-muted hover:text-bf-white')
            }
          >
            {t('settings.tabFourEyes')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'llm'}
            data-testid="settings-tab-llm"
            onClick={() => setTab('llm')}
            className={
              'px-4 py-2 text-sm transition-colors ' +
              (effectiveTab === 'llm'
                ? 'border-b-2 border-bf-gold text-bf-white'
                : 'border-b-2 border-transparent text-bf-muted hover:text-bf-white')
            }
          >
            {t('settings.tabLlm')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'profile'}
            data-testid="settings-tab-profile"
            onClick={() => setTab('profile')}
            className={
              'px-4 py-2 text-sm transition-colors ' +
              (effectiveTab === 'profile'
                ? 'border-b-2 border-bf-gold text-bf-white'
                : 'border-b-2 border-transparent text-bf-muted hover:text-bf-white')
            }
          >
            {t('settings.tabProfile')}
          </button>
        </div>
      )}

      {effectiveTab === 'users' && isAdmin ? (
        <UserManagement />
      ) : effectiveTab === 'fourEyes' && isAdmin ? (
        <FourEyesManagement />
      ) : effectiveTab === 'llm' && isAdmin ? (
        // Der LLM-Zugang gehört zum PROJEKT, nicht zur Instanz — ohne aktives Projekt gibt es
        // nichts zu konfigurieren, und der Hinweis sagt das, statt ein leeres Formular zu zeigen.
        activeProjectId ? (
          <LlmConfigPanel projectId={activeProjectId} />
        ) : (
          <p className="text-sm text-bf-muted" data-testid="settings-llm-no-project">
            {t('settings.llm.keinProjekt')}
          </p>
        )
      ) : (
        <MyProfile me={me} onMfaChanged={loadMe} />
      )}
    </div>
  );
}
